import {DatePipe} from '@angular/common';
import {Injectable} from '@angular/core';
import {take} from 'rxjs/operators';
import {DateUtil} from '../util/DateUtil';
import {ErrorDialogComponent} from '../dialogs/error-dialog/error-dialog.component';
import {MatDialog} from '@angular/material/dialog';
import {MatSnackBar} from '@angular/material/snack-bar';
import {KeycloakService} from 'keycloak-angular';
import {Observable, ReplaySubject} from 'rxjs';
import {KeycloakProfile} from 'keycloak-js';
import {HelpDTO} from '../../other/models/HelpDTO';
import {HelpService} from '../../other/services/help.service';
import {GenericListDialogComponent} from '../dialogs/generic-list-dialog/generic-list-dialog.component';
import {UserService} from '../../other/services/user.service';
import {UserDTO} from '../../clm/models/user/UserDTO';
import {BrokerService} from '../../onboarding-admin-portal/services/broker.service';
import {BrokerDTO} from '../../onboarding-admin-portal/models/broker/BrokerDTO';
import {Role} from '../../clm/types/role.enum';
import {AccessLevel} from '../types/feature';

@Injectable({
  providedIn: 'root'
})
export class SharedService {
  readonly dateFormat = DateUtil.dateFormat;
  private helpInfo: HelpDTO[];
  private profile: KeycloakProfile;
  private user: UserDTO;
  private broker: BrokerDTO;
  private loggedInKeycloakProfile$ = new ReplaySubject<KeycloakProfile>(1);
  private loggedInUserProfile$ = new ReplaySubject<UserDTO>(1);
  private loggedInBrokerProfile$ = new ReplaySubject<BrokerDTO>(1);


  constructor(private dialog: MatDialog,
              private datePipe: DatePipe,
              private helpService: HelpService,
              private snackBar: MatSnackBar,
              private keycloakService: KeycloakService,
              private userService: UserService,
              private brokerService: BrokerService) {
    this.getKeycloakAndUserProfile();
  }

  // Method use for static conversion instead of using DI for DatePipe
  static formatDate(date: Date, format: string) {
    const datePipe = new DatePipe('en-US');
    return datePipe.transform(date, format); // Use DatePipe to transform the date
  }

  formatDate(date: Date, format: string): string {
    return this.datePipe.transform(new Date(date), format);
  }

  formatDateHandleNull(date: Date): string {
    if (date){
      return this.formatDate(date, DateUtil.dateFormat);
    } else {
      return '';
    }
  }

  openSnackBar(message: string, action: string = 'Dismiss', milliseconds: number = 10000) {
    this.snackBar.open(message, action, {
      duration: milliseconds
    });
  }

  openErrorDialog(errorMessage: string) {
    return this.dialog.open(ErrorDialogComponent, {
      width: '40vw',
      data: errorMessage
    });
  }

  // TODO: Change to getLoggedInFullName
  getLoggedInUserName() {
    if (!this.profile) {
      return;
    }
    return `${this.profile.firstName} ${this.profile.lastName}`;
  }

  getProfileUsername(): string | undefined {
    if (!this.profile) {
      return;
    }
    return this.profile.username;
  }

  getProfileLinkedEntityCode(): string | undefined {
    const linkedEntityCode = this.user?.linkedEntityCode ?? this.getProfileUsername();
    if (!linkedEntityCode) {
      return;
    }
    return linkedEntityCode;
  }

  // TODO: Change to getLoggedInKeycloakProfile
  getLoggedInProfile$(): Observable<KeycloakProfile> {
    return this.loggedInKeycloakProfile$.asObservable();
  }

  getLoggedInUserProfile$(): Observable<UserDTO> {
    return this.loggedInUserProfile$.asObservable();
  }

  getLoggedInBrokerProfile$(): Observable<BrokerDTO> {
    return this.loggedInBrokerProfile$.asObservable();
  }

  getHelpInfo(componentName: string) {
    this.helpService.getListOfHelpInfo(componentName)
      .pipe(
        take(1)
      )
      .subscribe(
        (response: HelpDTO[]) => {
          if (this.helpInfo && this.helpInfo.length > 0) {
            this.helpInfo = [].concat(this.helpInfo, response);
          }
          else {
            this.helpInfo = response;
          }
        },
        (errorResponse) => {
          const error = errorResponse.error;
          this.openErrorDialog(error.businessMessage);
        }
      );
  }

  getInfoTooltip(inputName: string): string {
    let message = '';
    if (this.helpInfo) {
      const helpInfo = this.helpInfo.find((help: HelpDTO) => help.inputName === inputName);
      message = helpInfo ? helpInfo.inputInfo : '';
      if (!message) {
        message = `Default ${inputName}`;
      }
    }
    return message;
  }

  openGenericDialog(displayHeading: string, list: Array<any>, columnName?: string): void {
    const displayList: string[] = [];
    for (let i = 0; i < list.length; i++) {
      const displayString = columnName ? list[i][columnName] : list[i].name; // default `name` field is accessed.
      displayList.push(displayString);
    }

    const dialogRef = this.dialog.open(GenericListDialogComponent, {
      width: '40vw',
      maxHeight: '50vh',
      data: {list: displayList, heading: displayHeading}
    });
  }

  private getKeycloakAndUserProfile(): void {
    this.keycloakService
      .loadUserProfile()
      .then((profile: KeycloakProfile) => {
        this.profile = profile;
        this.loggedInKeycloakProfile$.next(this.profile);
        this.getUserProfile(this.profile.username);
      })
      .catch((error) => {
        console.error(error);
        return;
      });
  }

  private getUserProfile(username: string): void {
    this.userService.getUserByUsername(username).pipe(
      take(1)
    ).subscribe({
      next: (user) => {
        this.user = user;
        this.loggedInUserProfile$.next(this.user);
        this.getBrokerProfile(this.getProfileLinkedEntityCode());
      },
      error: (error) => {
        console.error(error);
        this.loggedInUserProfile$.next(null);
        return;
      }
    });
  }

  private getBrokerProfile(brokerCode: string): void {
    const isLoggedInUserABroker = this.user?.userRoles?.map(x => x.role_Name)?.includes(Role.Broker);
    if (!isLoggedInUserABroker) {
      this.loggedInBrokerProfile$.next(null);
      return;
    }
    this.brokerService.getBrokerByNumber(brokerCode).pipe(
      take(1)
    ).subscribe({
      next: (broker) => {
        this.broker = broker;
        this.loggedInBrokerProfile$.next(this.broker);
      },
      error: (error) => {
        console.error(error);
        this.loggedInBrokerProfile$.next(null);
        return;
      }
    });
  }

  getBrokerFeatureAccess(featureId: number): boolean {
    const brokerFeature = this.broker?.features.find(feature => feature.featureId === featureId);
    if (!brokerFeature) {
      return false;
    }
    return brokerFeature.accessLevel?.value == AccessLevel.Allow;
  }

}
